import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Play, Eye, Trash2, FileText, User, LogOut, CalendarClock, Loader2, Cloud, Edit2, Cog, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { storage, OBSERVATION_CONTEXTS } from '../lib/storage';
import { fetchCloudSessions, deleteCloudSession, cloudToLocalSession } from '../lib/cloudSessionService';
import { formatDate, formatTime } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { safeGet } from '../lib/safeFetch';
import { PullToRefresh } from '../components/PullToRefresh';
import { SwipeablePageWrapper } from '../components/SwipeablePageWrapper';

const API_URL = '';

export default function HomePage() {
  const navigate = useNavigate();
  const { user, logout, isCoachDeveloper } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [upcomingObservations, setUpcomingObservations] = useState([]);
  const [loadingUpcoming, setLoadingUpcoming] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [coachDevs, setCoachDevs] = useState([]);
  const [selectedObserverId, setSelectedObserverId] = useState(null);

  // Redirect coach users to their view
  useEffect(() => {
    if (user?.role === 'coach' && user?.linked_coach_id) {
      navigate(`/coach-view/${user.linked_coach_id}`);
    }
  }, [user, navigate]);

  // Load coach developers in the org
  useEffect(() => {
    if (isCoachDeveloper) {
      safeGet(`/api/org/coach-developers`).then(res => {
        if (res.ok && Array.isArray(res.data)) {
          setCoachDevs(res.data);
        }
      });
    }
  }, [isCoachDeveloper]);

  useEffect(() => {
    loadSessions(selectedObserverId);
  }, [selectedObserverId]);

  const loadSessions = async (observerId = null) => {
    setLoadingSessions(true);
    setLoadingUpcoming(true);
    try {
      // Try to load from cloud first
      const result = await fetchCloudSessions(observerId);
      if (result.success) {
        // Cloud fetch succeeded - use cloud data even if empty (new user = no sessions)
        const cloudSessions = (Array.isArray(result.data) ? result.data : []).map(s => ({
          id: s.session_id,
          name: s.name,
          coachId: s.coach_id,
          coachName: s.coach_name,
          status: s.status,
          observationContext: s.observation_context,
          createdAt: s.created_at,
          updatedAt: s.updated_at,
          plannedDate: s.planned_date,
          totalDuration: s.total_duration,
          events: { length: s.event_count || 0 }
        }));
        
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        
        // Separate sessions into upcoming (planned with future/today date) and regular
        const upcoming = [];
        const regular = [];
        
        cloudSessions.forEach(session => {
          // Check if this is a planned session with a future or today date
          if (session.status === 'planned' && session.plannedDate) {
            const plannedDate = new Date(session.plannedDate);
            const plannedDay = new Date(plannedDate.getFullYear(), plannedDate.getMonth(), plannedDate.getDate());
            
            // If planned date is today or in the future, show in upcoming
            if (plannedDay >= today) {
              upcoming.push(session);
            } else {
              // Expired planned session - show in regular sessions
              regular.push(session);
            }
          } else if (session.status === 'planned') {
            // Planned but no date - show in upcoming anyway
            upcoming.push(session);
          } else {
            // All other sessions (draft, active, completed) go to regular
            regular.push(session);
          }
        });
        
        // Sort upcoming by planned date (ascending - soonest first)
        upcoming.sort((a, b) => {
          const dateA = a.plannedDate ? new Date(a.plannedDate) : new Date();
          const dateB = b.plannedDate ? new Date(b.plannedDate) : new Date();
          return dateA - dateB;
        });
        
        // Sort regular by updated date (descending - most recent first)
        regular.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
        
        setUpcomingObservations(upcoming);
        setSessions(regular);
      } else {
        // Cloud fetch FAILED (network error, offline) - fall back to localStorage
        const localSessions = storage.getSessions() || [];
        const sorted = localSessions.sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
        
        // Also separate local sessions
        const upcoming = sorted.filter(s => s.status === 'planned');
        const regular = sorted.filter(s => s.status !== 'planned');
        
        setUpcomingObservations(upcoming);
        setSessions(regular);
      }
    } catch (err) {
      console.error('Failed to load sessions:', err);
      // Fall back to localStorage
      const localSessions = storage.getSessions() || [];
      setSessions(localSessions.sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0)));
    } finally {
      setLoadingSessions(false);
      setLoadingUpcoming(false);
    }
  };

  const handleDelete = async (sessionId) => {
    // Delete from localStorage
    storage.deleteSession(sessionId);
    
    // Delete from cloud
    try {
      await deleteCloudSession(sessionId);
    } catch (err) {
      console.error('Failed to delete from cloud:', err);
    }
    
    // Refresh list
    loadSessions();
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'planned':
        return <Badge className="bg-blue-500 hover:bg-blue-500">Planned</Badge>;
      case 'active':
        return <Badge className="bg-orange-500 hover:bg-orange-500">In Progress</Badge>;
      case 'completed':
        return <Badge className="bg-green-600 hover:bg-green-600">Completed</Badge>;
      default:
        return <Badge variant="secondary">Draft</Badge>;
    }
  };

  // Pull-to-refresh handler
  const handleRefresh = useCallback(async () => {
    await loadSessions(selectedObserverId);
  }, [selectedObserverId]);

  return (
    <SwipeablePageWrapper>
      <PullToRefresh onRefresh={handleRefresh} className="min-h-screen bg-slate-50">
        {/* Main Content */}
        <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 pb-16">
        {/* Create New Session */}
        <Card 
          className="mb-6 sm:mb-8 cursor-pointer hover:border-slate-400 transition-colors group"
          onClick={() => navigate('/session/new')}
          data-testid="create-session-card"
        >
          <CardContent className="flex items-center justify-center py-6 sm:py-8">
            <div className="text-center">
              <div className="w-12 sm:w-16 h-12 sm:h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3 sm:mb-4 group-hover:bg-slate-200 transition-colors">
                <Plus className="w-6 sm:w-8 h-6 sm:h-8 text-slate-600" />
              </div>
              <h2 className="text-base sm:text-lg font-semibold text-slate-900 font-['Manrope']">New Observation Session</h2>
              <p className="text-xs sm:text-sm text-slate-500 mt-1">Start observing a coaching session</p>
            </div>
          </CardContent>
        </Card>

        {/* Upcoming Observations - Coach Developer only */}
        {isCoachDeveloper() && (
          <Card className="mb-6 sm:mb-8">
            <CardHeader className="pb-3">
              <CardTitle className="text-base sm:text-lg font-['Manrope'] flex items-center gap-2">
                <CalendarClock className="w-5 h-5 text-emerald-600" />
                Upcoming Observations
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingUpcoming ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : upcomingObservations.length === 0 ? (
                <div className="text-center py-6">
                  <CalendarClock className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">No upcoming observations scheduled</p>
                  <Button 
                    variant="link" 
                    size="sm" 
                    onClick={() => navigate('/calendar')}
                    className="mt-2"
                  >
                    Schedule an observation
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {upcomingObservations.slice(0, 5).map((obs) => (
                    <div 
                      key={obs.id} 
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                      data-testid={`upcoming-session-${obs.id}`}
                    >
                      <div 
                        className="flex items-center gap-3 flex-1 cursor-pointer"
                        onClick={() => navigate(`/session/${obs.id}/setup`)}
                      >
                        <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                          <User className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{obs.name}</p>
                          <p className="text-sm text-slate-500">
                            {obs.coachName && <span>{obs.coachName} • </span>}
                            {obs.plannedDate ? new Date(obs.plannedDate).toLocaleDateString('en-GB', {
                              weekday: 'short',
                              day: 'numeric',
                              month: 'short'
                            }) : 'Date TBD'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-blue-500 hover:bg-blue-500">Planned</Badge>
                        <Button 
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/session/${obs.id}/setup`);
                          }}
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4 text-slate-500" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button 
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                              onClick={(e) => e.stopPropagation()}
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Planned Session?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will permanently delete "{obs.name}". This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDelete(obs.id)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        <Button 
                          size="sm"
                          className="bg-orange-500 hover:bg-orange-600"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/session/${obs.id}/observe`);
                          }}
                        >
                          <Play className="w-4 h-4 mr-1" />
                          Start
                        </Button>
                      </div>
                    </div>
                  ))}
                  {upcomingObservations.length > 5 && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="w-full mt-2"
                      onClick={() => navigate('/calendar')}
                    >
                      View all ({upcomingObservations.length}) in calendar
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Sessions List */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            {coachDevs.length > 1 ? (
              <Select
                value={selectedObserverId || user?.user_id || ''}
                onValueChange={(val) => setSelectedObserverId(val === user?.user_id ? null : val)}
              >
                <SelectTrigger className="w-auto h-auto border-0 shadow-none p-0 gap-1.5 focus:ring-0" data-testid="observer-selector">
                  <span className="text-lg font-semibold text-slate-900 font-['Manrope']">
                    {selectedObserverId && selectedObserverId !== user?.user_id
                      ? `${coachDevs.find(d => d.user_id === selectedObserverId)?.name || 'Unknown'}'s Sessions`
                      : 'Your Sessions'}
                  </span>
                </SelectTrigger>
                <SelectContent>
                  {coachDevs.map(dev => (
                    <SelectItem key={dev.user_id} value={dev.user_id}>
                      {dev.user_id === user?.user_id ? `Your Sessions` : `${dev.name}'s Sessions`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <h2 className="text-lg font-semibold text-slate-900 font-['Manrope']">
                Your Sessions
              </h2>
            )}
          </div>

          {sessions.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No sessions yet. Create your first observation session above.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {sessions.map((session) => (
                <Card key={session.id} className="hover:shadow-md transition-shadow" data-testid={`session-card-${session.id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg font-['Manrope']">{session.name}</CardTitle>
                        <CardDescription>{formatDate(session.createdAt)}</CardDescription>
                      </div>
                      {getStatusBadge(session.status)}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4 text-sm text-slate-600 mb-4">
                      <span>{session.events?.length || 0} events</span>
                      <span>•</span>
                      <span>{formatTime(session.totalDuration || 0)} duration</span>
                    </div>
                    <div className="flex gap-2">
                      {!selectedObserverId && session.status === 'draft' && (
                        <Button 
                          size="sm" 
                          onClick={() => navigate(`/session/${session.id}/setup`)}
                          data-testid={`edit-session-${session.id}`}
                        >
                          <Cog className="w-4 h-4 mr-1" />
                          Setup
                        </Button>
                      )}
                      {!selectedObserverId && (session.status === 'draft' || session.status === 'active') && (
                        <Button 
                          size="sm"
                          className="bg-orange-500 hover:bg-orange-600"
                          onClick={() => navigate(`/session/${session.id}/observe`)}
                          data-testid={`observe-session-${session.id}`}
                        >
                          <Play className="w-4 h-4 mr-1" />
                          {session.status === 'active' ? 'Resume' : 'Start'}
                        </Button>
                      )}
                      {session.status === 'completed' && (
                        <Button 
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/session/${session.id}/review`)}
                          data-testid={`review-session-${session.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Review
                        </Button>
                      )}
                      {!selectedObserverId && session.events?.length > 0 && session.status !== 'completed' && (
                        <Button 
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/session/${session.id}/review`)}
                          data-testid={`view-data-${session.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View Data
                        </Button>
                      )}
                      {!selectedObserverId && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Session?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will permanently delete "{session.name}" and all its data. This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction 
                              onClick={() => handleDelete(session.id)}
                              className="bg-red-600 hover:bg-red-700"
                              data-testid={`confirm-delete-${session.id}`}
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
      </PullToRefresh>
    </SwipeablePageWrapper>
  );
}
